/**
 * Copyright ©2026 Drivepoint
 */

import React, {useEffect, useMemo, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete, type AutocompleteRenderGetTagProps, AutocompleteRenderInputParams, Avatar, Box, Checkbox, Chip,
  CircularProgress, Grid, IconButton, MenuItem, Select, SelectChangeEvent, Typography
} from "@mui/material";
import type {Props} from "mui-modal-provider/dist/types";
import {closeSnackbar} from "notistack";
import DPButton from "@components/DPButton/DPButton";
import DPDialog from "@components/DPDialog/DPDialog";
import DPTextField from "@components/DPFields/DPTextField/DPTextField";
import DPIcon, {type DPIconName} from "@components/DPIcon/DPIcon";
import useStateChange from "@hooks/useStateChange";
import {buildShareables} from "@services/acl/manageAccess";
import Toast from "@services/Toast";
import StringUtils from "@utilities/StringUtils";
import "./DPShareModal.css";

export type ShareableItem = {
  id: string;
  name: string;
  email?: string;
  type: "user" | "group";
  icon?: string;
  memberCount?: number;
};

export type AccessEntry = {
  id: string;
  name: string;
  email?: string;
  type: "user" | "group";
  role: "owner" | "can_view" | "can_edit";
  icon?: string;
  memberCount?: number;
};

export type DPShareModalProps = {
  resourceName: string;
  shareableItems?: ShareableItem[];
  initialAccessList?: AccessEntry[];
  load?: () => Promise<{shareableItems: ShareableItem[]; initialAccessList: AccessEntry[]}>;
  onSave: (accessList: AccessEntry[]) => void | Promise<void>;
} & Props;

export default function DPShareModal(props: DPShareModalProps) {
  const {resourceName, onSave} = props;

  const company = useStateChange<any>("company");
  const [fetchedItems, setFetchedItems] = useState<ShareableItem[]>([]);
  const [loadedShareables, setLoadedShareables] = useState<ShareableItem[] | undefined>(undefined);
  const [loadedAccess, setLoadedAccess] = useState<AccessEntry[] | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(!!props.load);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (props.shareableItems || props.load || !company?.id) { return; }
    let cancelled = false;
    buildShareables(company.id).then(items => { if (!cancelled) { setFetchedItems(items); } }).catch(() => {});
    return () => { cancelled = true; };
  }, [company?.id, props.shareableItems, props.load]);

  const shareableItems = useMemo(() => props.shareableItems ?? loadedShareables ?? fetchedItems, [props.shareableItems, loadedShareables, fetchedItems]);
  const initialAccessList = useMemo(() => props.initialAccessList ?? loadedAccess ?? [], [props.initialAccessList, loadedAccess]);

  const [addRole, setAddRole] = useState<"can_view" | "can_edit">("can_view");
  const [draftAccessList, setDraftAccessList] = useState<AccessEntry[]>(() => [...initialAccessList]);
  const [pendingEntryIds, setPendingEntryIds] = useState<string[]>([]);

  useEffect(() => {
    if (!props.load) { return; }
    let cancelled = false;
    setLoading(true);
    props.load()
      .then(result => {
        if (cancelled) { return; }
        setLoadedShareables(result.shareableItems);
        setLoadedAccess(result.initialAccessList);
        setDraftAccessList([...result.initialAccessList]);
      })
      .catch(error => { logger.error(error); })
      .finally(() => { if (!cancelled) { setLoading(false); } });
    return () => { cancelled = true; };
  }, [props.load]);

  const hasChanges = useMemo(() => {
    if (draftAccessList.length !== initialAccessList.length) return true;
    return draftAccessList.some((entry, index) => {
      const initial = initialAccessList[index];
      return entry.id !== initial.id || entry.role !== initial.role;
    });
  }, [draftAccessList, initialAccessList]);

  const selectedItems = useMemo(() => {
    const pendingIds = new Set(pendingEntryIds);
    return draftAccessList
      .filter(entry => pendingIds.has(entry.id))
      .map(entry => ({...entry, email: entry.email}));
  }, [draftAccessList, pendingEntryIds]);

  function handleAddRoleChange(event: SelectChangeEvent<string>) {
    const role = event.target.value as "can_view" | "can_edit";
    const pendingIds = new Set(pendingEntryIds);
    setAddRole(role);
    setDraftAccessList(prev => prev.map(entry => pendingIds.has(entry.id) ? {...entry, role} : entry));
  }

  function handleAccessRoleChange(entryId: string, newRole: string) {
    setDraftAccessList(prev => prev.map(entry =>
      entry.id === entryId ? {...entry, role: newRole as AccessEntry["role"]} : entry
    ));
  }

  function handleRemoveAccess(entryId: string) {
    setDraftAccessList(prev => prev.filter(entry => entry.id !== entryId));
    setPendingEntryIds(prev => prev.filter(id => id !== entryId));
  }

  function handleSelectedItemsChange(_event: React.SyntheticEvent, newValue: ShareableItem[]) {
    const previousPendingIds = new Set(pendingEntryIds);
    const nextPendingIds = newValue.map(item => item.id);
    const nextPendingIdsSet = new Set(nextPendingIds);
    const addedItems = newValue.filter(item => !previousPendingIds.has(item.id));
    setDraftAccessList(prev => {
      const next = prev.filter(entry => !previousPendingIds.has(entry.id) || nextPendingIdsSet.has(entry.id));
      return [...next, ...addedItems.map(item => ({...item, role: addRole}))];
    });
    setPendingEntryIds(nextPendingIds);
  }

  async function handleSave() {
    setSaving(true);
    const progressKey = Toast.progress("Updating access...");
    try {
      await onSave(draftAccessList);
    } finally {
      if (progressKey !== undefined) { closeSnackbar(progressKey); }
      setSaving(false);
    }
    props.onClose();
  }

  function handleClose() {
    props.onClose();
  }

  function getAvailableOptions(): ShareableItem[] {
    const existingIds = new Set(draftAccessList.map(entry => entry.id));
    return shareableItems.filter(item => !existingIds.has(item.id));
  }

  function renderOptionAvatar(item: ShareableItem | AccessEntry) {
    if (item.type === "group") {
      return <Avatar sx={{bgcolor: "var(--BLUE-50)", color: "var(--GREY-700)", width: 40, height: 40}}>
        <DPIcon iconName={item.icon as DPIconName} />
      </Avatar>;
    }
    return <Avatar sx={{bgcolor: "var(--GREY-300)", width: 40, height: 40}}>
      {StringUtils.initials(item.name ?? item.email ?? "")}
    </Avatar>;
  }

  function renderOption(props: React.HTMLAttributes<HTMLLIElement> & { key: string }, option: ShareableItem, state: { selected: boolean }) {
    const {key, ...rest} = props;
    return <MenuItem key={key} {...rest}>
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Checkbox checked={state.selected} size="small" />
        {renderOptionAvatar(option)}
        <Box display="flex" flexDirection="column">
          <Typography variant="body1" fontWeight={500} fontSize={14}>{option.name}</Typography>
          <Typography variant="body1" fontWeight={400} fontSize={12} color="text.secondary">
            {option.type === "group" ? `${option.memberCount ?? 0} members` : option.email}
          </Typography>
        </Box>
      </Box>
    </MenuItem>;
  }

  function renderAccessItemInfo(entry: AccessEntry) {
    return <div className="dp-share-modal-access-item-info">
      <Typography variant="body1" fontWeight={500} fontSize={14}>{entry.name}</Typography>
      <Typography variant="body1" fontWeight={400} fontSize={12} color="text.secondary">
        {entry.type === "group" ? `${entry.memberCount ?? 0} members` : entry.email}
      </Typography>
    </div>;
  } 

  function renderRoleSelect(value: string, onChange: (event: SelectChangeEvent<string>) => void) {
    return <Select size="small" className="dp-share-modal-access-item-role" value={value} onChange={onChange}>
      <MenuItem value="can_view">Can view</MenuItem>
      <MenuItem value="can_edit">Can edit</MenuItem>
    </Select>;
  }

  function renderAccessItem(entry: AccessEntry) {
    const isOwner = entry.role === "owner";
    const isNew = pendingEntryIds.includes(entry.id);
    const itemClassName = `dp-share-modal-access-item${isNew ? " dp-share-modal-access-item-new" : ""}`;
    return <div key={entry.id} className={itemClassName}>
      {renderOptionAvatar(entry)}
      {renderAccessItemInfo(entry)}
      {!isOwner && renderRoleSelect(entry.role, (event) => handleAccessRoleChange(entry.id, event.target.value))}
      {!isOwner && <IconButton size="small" onClick={() => handleRemoveAccess(entry.id)} sx={{color: "var(--RED-500)"}}>
        <CloseIcon fontSize="small" />
      </IconButton>}
    </div>;
  }

  function renderActions() {
    return <>
      {hasChanges && <Typography variant="body2" color="text.secondary" fontSize={11} fontStyle="italic">Pending changes</Typography>}
      <DPButton label="CANCEL" variant="text" onClick={handleClose} />
      <DPButton label="SAVE" onClick={handleSave} disabled={loading || saving} />
    </>;
  }

  function renderTitle() {
    return <Box display="flex" flexDirection="row" alignItems="center">
      <Typography variant="body1" fontWeight={500} fontSize={20}>Manage Access to</Typography>
      <Typography variant="body1" fontWeight={500} fontSize={20} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{`"${resourceName}"`}</Typography>
    </Box>;
  }

  function renderAutocompleteTags(value: ShareableItem[], getTagProps: AutocompleteRenderGetTagProps) {
    return value.map((option, index) => {
      const {key, ...tagProps} = getTagProps({index});
      return <Chip key={key} label={option.email ?? option.name} size="small" {...tagProps} />;
    });
  }

  function renderAutocompleteInput(params: AutocompleteRenderInputParams) {
    return <DPTextField {...params} size="small" label="Add users or groups" placeholder="Enter name, email, or group..." />;
  }

  function renderAutocompleteField() {
    return <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      sx={{flex: 1}}
      slotProps={{popper: {className: "dp-share-modal-popper"}}}
      options={getAvailableOptions()}
      getOptionLabel={(option) => option.name}
      value={selectedItems}
      onChange={handleSelectedItemsChange}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderOption={renderOption}
      renderTags={renderAutocompleteTags}
      renderInput={renderAutocompleteInput}
    />;
  }

  function renderAddRoleSelect() {
    return renderRoleSelect(addRole, handleAddRoleChange);
  }

  function renderAccessList() {
    return <div className="dp-share-modal-access-list">
      {draftAccessList.map(entry => renderAccessItem(entry))}
    </div>;
  }

  return <DPDialog isOpen={!!props.open} handleClose={handleClose} title={renderTitle()} className="dp-share-modal" actions={renderActions()}>
    {loading
      ? <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}><CircularProgress /></Box>
      : <Grid container spacing={2}>
        <Grid flex={1}>{renderAutocompleteField()}</Grid>
        <Grid>{renderAddRoleSelect()}</Grid>
        <Grid size={{xs: 12}}>
          <div className="dp-share-modal-access-section">
            <Typography className="dp-share-modal-access-label">People with access</Typography>
            {renderAccessList()}
          </div>
        </Grid>
      </Grid>}
  </DPDialog>;
}
